import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getUrlParamsFetch, handleAxiosError } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import {
  AlertSeverityEnum,
  UseFetchEnabledParams,
  UseMutationsParams,
} from '@/shared/interfaces';
import {
  ICausalIncidencia,
  ICausalIncidenciaPagingRes,
} from '@/shared/interfaces/erp/tickets-soporte-clientes';
import { useUiStore } from '@/store/ui';

import {
  USE_MOCK,
  MOCK_CAUSAL_INCIDENTES_PAGING,
  MOCK_CAUSAL_INCIDENTES,
  mockAxiosResponse,
} from '@/shared/actions/erp/tickets-soporte-cliente/tickets-mock-data';

const { get, post, put } = erpApi();

///* tanSctack query
export const useFetchCausalIncidencia = ({
  page = 1,
  page_size = 10,
  nombre = '',
  numero_referencia,
  descripcion,
  categoria_incidencia,
  subcategoria_incidencia,
  enabled = true,
}: GetCausalIncidenciaParams) => {
  return useQuery({
    queryKey: ['causalincidencia', page, nombre,numero_referencia,descripcion,categoria_incidencia,subcategoria_incidencia, page_size],
    queryFn: async () => {
      if (USE_MOCK) return mockAxiosResponse(MOCK_CAUSAL_INCIDENTES_PAGING);
      return getCausalIncidencias({ page, nombre,numero_referencia,descripcion,categoria_incidencia,subcategoria_incidencia, page_size });
    },
    enabled,
  });
};

export const useFetchCausalIncidenciaEnabled = ({
  enabled = true,
  params,
}: UseFetchEnabledParams<GetCausalIncidenciaParams>) => {
  return useQuery({
    queryKey: ['causalincidencia', params],
    queryFn: async () => {
      if (USE_MOCK) return mockAxiosResponse(MOCK_CAUSAL_INCIDENTES_PAGING);
      return getCausalIncidencias(params);
    },
    enabled,
  });
};

export const useGetCausalIncidencia = (id: string) => {
  return useQuery({
    queryKey: ['causalincidencias', id],
    queryFn: async () => {
      if (USE_MOCK) {
        const item = MOCK_CAUSAL_INCIDENTES.find((c) => c.id_causal_incidencia === id) || MOCK_CAUSAL_INCIDENTES[0];
        return mockAxiosResponse(item);
      }
      return getCausalIncidencia(id);
    },
  });
};

export const useCreateCausalIncidencia = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createCausalIncidencia,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['causalincidencia'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Categoria de producto creada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdateCausalIncidencia = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updateCausalIncidencia,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['causalincidencia'] });
      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ??
            'Categoria de producto actualizada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

///* axios
export type GetCausalIncidenciaParams = {
  page?: number;
  page_size?: number;
  nombre?: string;
  numero_referencia?:string;
  descripcion?:string;
  categoria_incidencia?:string,
  subcategoria_incidencia?:string,
  enabled?: boolean,
};

export type CreateCausalIncidenciaParams = Omit<
  ICausalIncidencia,
  'id_causal_incidencia'
>;
export type UpdateCausalIncidenciaParams = {
  id: string;
  data: CreateCausalIncidenciaParams;
};

export const getCausalIncidencias = async (
  params?: GetCausalIncidenciaParams
) => {
  const queryParams = getUrlParamsFetch({ ...params });

  return get<ICausalIncidenciaPagingRes>(
    `${urlBackend}api/v1/causalincidencia/?${queryParams}`,
    true
  );
};

export const getCausalIncidencia = async (id: string) => {
  try {
    return await get<ICausalIncidencia>(
      `${urlBackend}api/v1/causalincidencia/${id}/`,
      true
    );
  } catch (error) {
    return handleAxiosError(error);
  }
};

export const createCausalIncidencia = async (
  data: CreateCausalIncidenciaParams
) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<ICausalIncidencia>(
    `${urlBackend}api/v1/causalincidencia/`,
    data,
    true
  );
};
export const updateCausalIncidencia = async ({
  id,
  data,
}: UpdateCausalIncidenciaParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return put<ICausalIncidencia>(
    `${urlBackend}api/v1/causalincidencia/${id}/`,
    data,
    true
  );
};
